/**
 * clearCellsInRange.ts - Pure mutation function for clearing cells within a selection box
 */

import produce from 'immer';

import {type Table} from 'notebook/tables/constants/TableConstants';
import {MultiSelectionBox} from 'notebook/tables/constants/TableSelectionConstants';
import {type AffectedCell, type TableMutationResult} from 'notebook/tables/excess/ops/TableOpConstants';
import TableUtil from 'notebook/tables/TableUtil';
import {getBaseCell} from 'notebook/tables/typed-values/DataTypes';

/**
 * Clear all cells within a selection box.
 *
 * @param table - The current table
 * @param selectionBox - The box defining which cells to clear
 * @returns The mutation result with new table, operation, and affected cells
 */
export function clearCellsInRange(table: Table, selectionBox: MultiSelectionBox): TableMutationResult {
  const affectedCells: Array<AffectedCell> = [];
  const tableOpCells: Array<{row: number; column: number}> = [];

  const newTable = produce(table, (draftTable) => {
    for (let i = selectionBox.minI; i <= selectionBox.maxI; i++) {
      for (let j = selectionBox.minJ; j <= selectionBox.maxJ; j++) {
        const oldCell = table.table[i]?.[j];
        if (oldCell != null) {
          const newCell = TableUtil.makeCell('', getBaseCell(oldCell));
          draftTable.table[i][j] = newCell;
          affectedCells.push({row: i, column: j, oldCell, newCell});
          tableOpCells.push({row: i, column: j});
        }
      }
    }
  });

  return {
    table: newTable,
    tableOp: {type: 'update-cells', cells: tableOpCells},
    affectedCells,
  };
}
